import { OrderDetail, ProductExtra, ProductOrder } from '@interfaces/OrderTypes';
import { DELIVERY_MAP, ORDER_PAYMENT_MAP, PRINT_TEXT_SIZE_MAP, PRINT_TEXT_SIZES_VALUES_MAP } from '@utils/constants';
import { formatToCurrency } from '@utils/formats';
import { PosPrintData, PosPrintTableField } from '@plick/electron-pos-printer';
import { consolidateProducts } from '@utils/print/consolidateProducts';
import { productLabel } from '@utils/print/productLabel';
import { getOrderProducts, getOrderFooter } from './index';
import { normalizePaymentMethod } from '../paymentUtils';
import { formatScheduledDateTime } from '../scheduledOrderUtils';

type Props = {
  order: OrderDetail;
  commerceLogo: string | null | undefined;
  commerceName: string;
  commerceAddress: string;
  referencesLabel?: string;
  textSize: keyof typeof PRINT_TEXT_SIZE_MAP;
  showLogo: boolean;
  showClientPhone: boolean;
  showCommerceAddress: boolean;
};

// Leyenda de pedido programado para el ticket POS (Electron). Extraída como helper para no
// aumentar la complejidad ciclomática de buildElectronOrderTicket. Vacía si no es programado.
const buildScheduledLegend = (
  pedido: OrderDetail['pedido'],
  textSize: (typeof PRINT_TEXT_SIZES_VALUES_MAP)[keyof typeof PRINT_TEXT_SIZES_VALUES_MAP],
): PosPrintData[] => {
  if (!pedido?.is_scheduled) return [];
  const fecha = pedido.scheduled_delivery_time ? formatScheduledDateTime(pedido.scheduled_delivery_time) : '';
  return [
    {
      type: 'text',
      value: 'PEDIDO PROGRAMADO',
      style: { textAlign: 'center', fontFamily: 'sans-serif', fontSize: textSize.title, fontWeight: '700' },
    },
    {
      type: 'text',
      value: `Entregar: ${fecha}`,
      style: {
        textAlign: 'center',
        fontFamily: 'sans-serif',
        fontSize: textSize.subTitle,
        fontWeight: '600',
        paddingBottom: '16px',
      },
    },
  ];
};

export const buildElectronOrderTicket = ({
  order,
  commerceLogo,
  commerceName,
  commerceAddress,
  referencesLabel: references,
  textSize: selectedTextSize,
  showLogo,
  showClientPhone,
  showCommerceAddress,
}: Props) => {
  const textSize = PRINT_TEXT_SIZES_VALUES_MAP[selectedTextSize];
  const referencesLabel = references || 'Referencias';
  const ticket: PosPrintData[] = [
    {
      type: 'image',
      url: commerceLogo || 'https://placehold.co/10x10',
      position: 'center',
      width: '110px',
      height: '110px',
      style: {
        display: showLogo ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Ticket pedido',
      style: {
        textAlign: 'center',
        fontFamily: 'sans-serif',
        fontSize: textSize.title,
        fontWeight: '600',
        paddingBottom: '2px',
      },
    },
    {
      type: 'text',
      value: `No. ${order.pedido.folio}`,
      style: {
        textAlign: 'center',
        fontFamily: 'sans-serif',
        fontSize: textSize.title,
        fontWeight: '600',
        paddingBottom: '16px',
      },
    },
    ...buildScheduledLegend(order.pedido, textSize),
    {
      type: 'text',
      value: order.pedido?.nombre ? 'Cliente:' : ' ',
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontWeight: '400',
        padding: '0 0 2px 0',
        fontFamily: 'sans-serif',
        marginTop: '10px',
      },
    },
    {
      type: 'text',
      value: order.pedido?.nombre || ' ',
      style: {
        textAlign: 'center',
        fontFamily: 'sans-serif',
        fontSize: textSize.subTitle,
        fontWeight: '600',
      },
    },
    {
      type: 'text',
      value: `Celular: ${order.pedido.country_code ? '+' + order.pedido.country_code + ' ' : ''} ${
        order.pedido.numero_celular
      }`,
      style: {
        textAlign: 'center',
        fontSize: textSize.medium,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        padding: '2px 0 10px 0',
        display: showClientPhone ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Mesa: ' + (order.pedido?.numero_mesa || order.pedido?.self_service?.table_number),
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        padding: `${showClientPhone ? '0' : '10px'} 0 10px 0`,
        display: order.pedido?.numero_mesa || order.pedido?.self_service?.table_number ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Detalle del pedido: ',
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingTop: '10px',
      },
    },
    {
      type: 'table',
      style: { fontSize: textSize.content, fontFamily: 'sans-serif' },
      tableHeader: [],
      tableBody: [
        [
          { type: 'text', value: 'Artículo', style: { textAlign: 'left', fontWeight: '600', padding: '5px 2px' } },
          { type: 'text', value: 'Total', style: { fontWeight: '600', padding: '5px 2px' } },
        ],
        [
          {
            type: 'divider',
            style: {
              paddingTop: '0px',
            },
          },
        ],
        ...getOrderProducts(order.productos),
        ...getOrderFooter(order.pedido),
      ],
      tableFooter: [],
      tableHeaderStyle: {},
      tableBodyStyle: {},
      tableFooterStyle: {},
      tableBodyCellStyle: {
        padding: '1px 2px',
      },
    },
    {
      type: 'text',
      value: 'Indicaciones: ' + order.pedido?.comentario,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '3px',
        paddingTop: '7px',
        display: order.pedido?.comentario ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Método de entrega: ' + DELIVERY_MAP[order.pedido.metodo_entrega]?.label,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '3px',
        paddingTop: !order.pedido?.comentario ? '7px' : '0',
      },
    },
    {
      type: 'text',
      value: (() => {
        let paymentLabel = ORDER_PAYMENT_MAP[order.pedido.metodo_pago]?.label || order.pedido.metodo_pago;
        if (normalizePaymentMethod(order.pedido.metodo_pago) === ORDER_PAYMENT_MAP.toConfirm.key) {
          paymentLabel = order.pedido.metodo_entrega === 'inRestaurant' ? 'Por confirmar' : 'Pago en línea';
        }
        return 'Tipo de pago: ' + paymentLabel;
      })(),
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '3px',
      },
    },
    {
      type: 'text',
      value: 'Cliente paga con: ' + formatToCurrency(parseFloat(order.pedido?.cliente_paga_con || '0')),
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '3px',
        display:
          order.pedido?.cliente_paga_con &&
          normalizePaymentMethod(order.pedido?.metodo_pago) === ORDER_PAYMENT_MAP.efectivo.key
            ? 'block'
            : 'none',
      },
    },
    {
      type: 'text',
      value: 'Cambio: ' + formatToCurrency(parseFloat(order.pedido?.cliente_paga_con || '0') - order.pedido.total),
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '3px',
        display:
          order.pedido?.cliente_paga_con &&
          normalizePaymentMethod(order.pedido?.metodo_pago) === ORDER_PAYMENT_MAP.efectivo.key
            ? 'block'
            : 'none',
      },
    },
    {
      type: 'text',
      value: 'Domicilio de entrega:',
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '2px',
        display: order.pedido?.metodo_entrega === 'delivery' ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Zona o Colonia: ' + order.pedido?.colonia,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: order.pedido?.colonia ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Calle o Mza: ' + order.pedido?.calle,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: order.pedido?.calle ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Numero Exterior: ' + order.pedido?.numero_exterior,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: order.pedido?.numero_exterior ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Numero interior: ' + order.pedido?.numero_interior,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: order.pedido?.numero_interior ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Dirección: ' + order.pedido?.direccion_completa,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: order.pedido?.direccion_completa ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: `${referencesLabel}: ${order.pedido?.referencias}`,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: order.pedido?.referencias ? 'block' : 'none',
      },
    },
    {
      type: 'divider',
      style: {
        paddingTop: '5px',
        borderBottomStyle: 'dotted',
        borderColor: 'gray',
      },
    },
    {
      type: 'text',
      value: commerceName,
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '2px',
        paddingTop: '5px',
        display: showCommerceAddress ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: commerceAddress,
      style: {
        textAlign: 'center',
        fontSize: textSize.small,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingBottom: '2px',
        display: showCommerceAddress ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Fecha y hora: ' + new Date(order.pedido.created_at).toLocaleString(),
      style: {
        textAlign: 'center',
        fontSize: textSize.small,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        margin: `${showCommerceAddress ? '4px' : '10px'} 0 0 0`,
      },
    },
    {
      type: 'text',
      value:
        'Este formato no es un comprobante de pago, ni contrato de venta, es para uso exclusivo del vendedor. Los precios que en el consignan son válidos hasta la fecha indicada.',
      style: {
        textAlign: 'center',
        fontSize: textSize.small,
        fontFamily: 'sans-serif',
        fontWeight: '400',
        paddingTop: '4px',
      },
    },
  ];

  return { ticket };
};

// Helper function for simplified kitchen ticket - products without prices
const getKitchenOrderProducts = (products: ProductOrder[]) => {
  const getExtrasSimplified = (extras: ProductExtra[]) => {
    const data: PosPrintTableField[][] = extras.map((extra) => {
      return [
        {
          type: 'text',
          value: `  + ${extra.cantidad} x ${extra.nombre}`,
          style: { textAlign: 'left', paddingLeft: '10px', fontSize: '10px' },
        },
      ];
    });
    return data;
  };

  // P0-A.3: consolida líneas idénticas (suma cantidades/subtotales, extras incluidos).
  const data: PosPrintTableField[][] = consolidateProducts(products).flatMap((product) => {
    return [
      [
        {
          type: 'text',
          // P0-A.1: omite ` - {presentacion}` si está vacía o es igual al nombre.
          value: productLabel(product),
          style: { textAlign: 'left', paddingRight: '5px', paddingTop: '4px', fontWeight: '600' },
        },
      ],
      product?.instrucciones
        ? [
            {
              type: 'text',
              value: `  Instrucciones: ${product.instrucciones}`,
              style: { textAlign: 'left', fontWeight: '700', paddingLeft: '10px', fontSize: '10px' },
            },
          ]
        : [],
      ...getExtrasSimplified(product.extras),
      [
        {
          type: 'divider',
          style: {
            paddingTop: '4px',
          },
        },
      ],
    ];
  });

  return data;
};

export const buildElectronKitchenTicket = ({
  order,
  commerceLogo,
  commerceName,
  textSize: selectedTextSize,
  showLogo,
}: Omit<Props, 'commerceAddress' | 'showCommerceAddress' | 'referencesLabel' | 'showClientPhone'>) => {
  const textSize = PRINT_TEXT_SIZES_VALUES_MAP[selectedTextSize];
  const ticket: PosPrintData[] = [
    {
      type: 'image',
      url: commerceLogo || 'https://placehold.co/10x10',
      position: 'center',
      width: '110px',
      height: '110px',
      style: {
        display: showLogo ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Ticket de Cocina',
      style: {
        textAlign: 'center',
        fontFamily: 'sans-serif',
        fontSize: textSize.title,
        fontWeight: '600',
        paddingBottom: '2px',
      },
    },
    {
      type: 'text',
      value: `No. ${order.pedido.folio}`,
      style: {
        textAlign: 'center',
        fontFamily: 'sans-serif',
        fontSize: textSize.title,
        fontWeight: '600',
        paddingBottom: '16px',
      },
    },
    {
      type: 'text',
      value: `Método: ${DELIVERY_MAP[order.pedido.metodo_entrega]?.label || order.pedido.metodo_entrega}`,
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        padding: '0 0 10px 0',
      },
    },
    {
      type: 'text',
      value: 'Mesa: ' + (order.pedido?.numero_mesa || order.pedido?.self_service?.table_number),
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        padding: '10px 0 10px 0',
        display: order.pedido?.numero_mesa || order.pedido?.self_service?.table_number ? 'block' : 'none',
      },
    },
    {
      type: 'text',
      value: 'Productos: ',
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingTop: '10px',
      },
    },
    {
      type: 'table',
      style: { fontSize: textSize.content, fontFamily: 'sans-serif' },
      tableHeader: [],
      tableBody: [...getKitchenOrderProducts(order.productos)],
      tableFooter: [],
      tableHeaderStyle: {},
      tableBodyStyle: {},
      tableFooterStyle: {},
      tableBodyCellStyle: {
        padding: '1px 2px',
      },
    },
    {
      type: 'text',
      value: 'Indicaciones: ' + order.pedido?.comentario,
      style: {
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '3px',
        paddingTop: '7px',
        display: order.pedido?.comentario ? 'block' : 'none',
      },
    },
    {
      type: 'divider',
      style: {
        paddingTop: '5px',
        borderBottomStyle: 'dotted',
        borderColor: 'gray',
      },
    },
    {
      type: 'text',
      value: commerceName,
      style: {
        textAlign: 'center',
        fontSize: textSize.content,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        paddingBottom: '2px',
        paddingTop: '5px',
      },
    },
    {
      type: 'text',
      value: 'Fecha y hora: ' + new Date(order.pedido.created_at).toLocaleString(),
      style: {
        textAlign: 'center',
        fontSize: textSize.small,
        fontFamily: 'sans-serif',
        fontWeight: '600',
        margin: '4px 0 0 0',
      },
    },
  ];

  return { ticket };
};
